import React, { useEffect, useState } from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { PlayerName } from "@/components/PlayerCardDialog";

const API = process.env.REACT_APP_BACKEND_URL;
const ST = { APPROVED: "bg-emerald-600 text-white", WATCH: "bg-amber-500/20 text-amber-300", PASS: "bg-slate-800 text-slate-400" };
const COMP = [["positional", "Pos"], ["archetype", "Arch"], ["form", "Form"], ["deployment", "Line/PP"], ["quality", "Data"]];

const Leg = ({ l }) => (
  <div className="flex-1 rounded-lg border border-slate-800 bg-slate-950/50 p-2.5 font-mono text-[10px]" data-testid={`v2-leg-${l.player_id}`}>
    <div className="flex items-center justify-between gap-2">
      <span className="truncate"><span className="text-cyan-400">#{l.jersey}</span> <PlayerName name={l.name} team={l.team} className="font-display text-[13px] font-semibold uppercase text-slate-100" /></span>
      <b className="text-cyan-300">{l.score}</b>
    </div>
    <p className="text-slate-500">{l.team} {l.pos} vs {l.opp} · {l.slot || (l.line ? `line ${l.line}` : "line ?")} · {l.pp || "no PP"} · {l.lineup}</p>
    <div className="mt-1.5 grid grid-cols-5 gap-1">
      {COMP.map(([k, lab]) => (
        <div key={k} className="rounded bg-slate-900 px-1 py-0.5 text-center" title={l.weights_used?.[k] != null ? `weight ${Math.round(l.weights_used[k] * 100)}%` : "missing, weight spread to others"}>
          <p className="text-[8px] uppercase text-slate-500">{lab}</p>
          <p className={l.components?.[k] == null ? "text-slate-600" : "text-slate-200"}>{l.components?.[k] ?? "—"}</p>
        </div>
      ))}
    </div>
    <p className="mt-1 text-emerald-300/80">{(l.reasons || []).join(" · ")}</p>
    <details className="mt-1 text-slate-400">
      <summary className="cursor-pointer text-slate-500 hover:text-slate-300">evidence</summary>
      <p>Opp GA/GP vs {l.pos}: {l.positional ? `${l.positional.opp_ga_gp} (league ${l.positional.league}, ${l.positional.opp_gp} GP)` : "invalid"}{l.shotpropz_ga != null && ` · ShotPropz ${l.shotpropz_ga}`}</p>
      <p>Form: {l.form ? `${l.form.g}G in ${l.form.gp} GP · L${l.form.l10_n} ${l.form.l10_g}G · est ${Math.round(l.form.p_goal * 100)}%/game` : "no history"}</p>
      <p>Archetype: {l.archetype || "—"}{l.archetype_rate ? ` · season ${l.archetype_rate.season.hit_pct}% (${l.archetype_rate.season.scorers}/${l.archetype_rate.season.players}) · L7 ${l.archetype_rate.last7?.hit_pct ?? "—"}% · adj ${l.archetype_rate.rate}%` : " · no graded rate"}</p>
      <p>PP pts/GP: {l.pp_rate ? `${l.pp_rate.rate} (${l.pp_rate.n} GP)` : "—"}{l.missing?.length ? ` · missing: ${l.missing.join(", ")}` : ""}</p>
    </details>
  </div>
);

const Row = ({ label, r }) => (
  <tr className="border-t border-slate-800/60">
    <td className="px-2 py-1 text-slate-400">{label}</td>
    <td className="px-2 py-1 text-right">{r?.pairs ?? "—"}</td>
    <td className="px-2 py-1 text-right font-bold text-slate-200">{r?.pair_hit_pct ?? "—"}%</td>
    <td className="px-2 py-1 text-right">{r?.legs ?? "—"}</td>
    <td className="px-2 py-1 text-right text-slate-200">{r?.leg_hit_pct ?? "—"}%</td>
  </tr>
);

const Backtest = ({ bt }) => !bt ? <p className="font-mono text-[11px] text-slate-500">Backtest not run yet.</p> : (
  <div className="space-y-2 font-mono text-[10px]" data-testid="v2-backtest">
    <p className={`rounded-lg border px-3 py-2 text-[11px] ${bt.verdict?.improved ? "border-emerald-700 bg-emerald-950/30 text-emerald-200" : "border-amber-700/50 bg-amber-950/20 text-amber-200"}`} data-testid="v2-verdict">
      {bt.verdict?.text}
    </p>
    <p className="text-slate-500">{bt.baseline?.slates} identical slates {bt.dates?.[0]}–{bt.dates?.[1]} · pre-game data only · candidate pool leg hit {bt.pool_leg_hit_pct}% ({bt.pool_legs} legs) · {bt.shared_pairs} pairs picked by both · last run {bt.generated_at?.slice(0, 16).replace("T", " ")} UTC</p>
    <p className="text-slate-500">Pair counts differ: v2's per-game and per-player caps leave fewer than 10 pairs on small slates (z-test accounts for it). History uses dressed players, so every historical v2 pair counts as lineup-confirmed; WATCH/PASS only appear live.</p>
    <div className="overflow-x-auto rounded-lg border border-slate-800">
      <table className="w-full min-w-[520px]">
        <thead className="bg-slate-900 text-[9px] uppercase tracking-wider text-slate-500">
          <tr><th className="px-2 py-1 text-left">Engine / tier</th><th className="px-2 py-1 text-right">Pairs</th><th className="px-2 py-1 text-right">Pair hit</th><th className="px-2 py-1 text-right">Legs</th><th className="px-2 py-1 text-right">Leg hit</th></tr>
        </thead>
        <tbody className="text-slate-300">
          {["baseline", "v2"].map((e) => (
            <React.Fragment key={e}>
              <Row label={<b className={e === "v2" ? "text-cyan-300" : "text-slate-200"}>{e === "v2" ? bt.version : "Baseline (current)"}</b>} r={bt[e]} />
              {Object.entries(bt[e]?.tiers || {}).map(([t, r]) => <Row key={t} label={`  rank ${t}`} r={r} />)}
            </React.Fragment>
          ))}
          {Object.entries(bt.by_season || {}).flatMap(([s, v]) => ["baseline", "v2"].map((e) => <Row key={s + e} label={`${s.slice(0, 4)}-${s.slice(6)} · ${e}`} r={v[e]} />))}
          {Object.entries(bt.v2_by_status || {}).map(([s, r]) => <Row key={s} label={`v2 ${s} (history: dressed = confirmed)`} r={r} />)}
        </tbody>
      </table>
    </div>
  </div>
);

export const PairingV2Panel = ({ apiDate }) => {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    setD(null); setErr(null);
    axios.get(`${API}/api/pairing-v2`, { params: apiDate ? { date: apiDate } : {}, timeout: 240000 })
      .then((r) => setD(r.data)).catch((e) => setErr(e?.response?.data?.detail || "Engine v2 unavailable"));
  }, [apiDate]);
  if (err) return <p className="py-10 text-center font-mono text-sm text-rose-400" data-testid="v2-error">{err}</p>;
  if (!d) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-cyan-400" /></div>;
  const w = d.config.weights;
  return (
    <div className="space-y-4" data-testid="v2-panel">
      <p className="font-mono text-[10px] text-slate-500" data-testid="v2-meta">
        {d.version} preview · weights {Object.entries(w).map(([k, v]) => `${k} ${Math.round(v * 100)}%`).join(" · ")} · pair = {Math.round(d.config.pair_mean_weight * 100)}% avg leg + {Math.round(d.config.pair_min_weight * 100)}% weaker leg · max {d.config.max_player_uses} pairs/player, {d.config.max_pairs_per_game} pairs/game · {d.candidates} candidates · default engine stays baseline
      </p>
      <div className="space-y-2" data-testid="v2-pairs">
        {d.pairs.map((p, i) => (
          <div key={p.pair_id} className="rounded-xl border border-slate-800 bg-slate-900/30 p-3" data-testid={`v2-pair-${i}`}>
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2 font-mono text-[11px]">
              <span className="font-bold uppercase tracking-wider text-slate-500">#{i + 1} · {p.legs[0].game_label} + {p.legs[1].game_label}</span>
              <span className="flex items-center gap-2">
                <span className={`rounded px-1.5 py-0.5 text-[9px] font-bold ${ST[p.status]}`} data-testid={`v2-status-${i}`}>{p.status}</span>
                <span className="rounded-lg border border-cyan-500/30 bg-cyan-950/30 px-2 py-1 font-bold text-cyan-300">{p.score}</span>
              </span>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">{p.legs.map((l) => <Leg key={l.player_id} l={l} />)}</div>
            <p className="mt-1.5 font-mono text-[10px] text-slate-400">{p.rationale}</p>
          </div>
        ))}
        {!d.pairs.length && <p className="py-6 text-center font-mono text-[11px] text-slate-600">No eligible cross-game pairs</p>}
      </div>
      <section className="rounded-xl border border-slate-800 bg-slate-900/30 p-3" data-testid="v2-compare">
        <p className="mb-2 font-mono text-[11px] font-bold uppercase tracking-wider text-slate-300">Tonight: baseline Top 10 vs v2 Top 10</p>
        <div className="grid grid-cols-1 gap-3 font-mono text-[10px] md:grid-cols-2">
          <ol className="space-y-0.5 text-slate-400">{d.baseline_top10.map((b) => <li key={b.rank}>{b.rank}. {b.label} <span className="text-slate-500">({b.players.filter(Boolean).join(" + ")})</span> · {b.combined_gapg} GA/GP</li>)}</ol>
          <ol className="space-y-0.5 text-slate-300">{d.pairs.map((p, i) => <li key={p.pair_id}>{i + 1}. {p.legs.map((l) => `${l.name} (${l.team} ${l.pos})`).join(" + ")} · {p.score} · {p.status}</li>)}</ol>
        </div>
      </section>
      <section className="rounded-xl border border-slate-800 bg-slate-900/30 p-3">
        <p className="mb-2 font-mono text-[11px] font-bold uppercase tracking-wider text-slate-300">Backtest: baseline vs v2 (player-level legs)</p>
        <Backtest bt={d.backtest} />
      </section>
    </div>
  );
};

export default PairingV2Panel;
